import { useEffect, useState } from 'react';

import { useDeviceViewport } from '@/hooks/useViewport';
import getRemoteSettings from '@/utils/user/getRemoteSettings';

interface MobilePresetFeedSetting {
  enabled: boolean;
}

const REMOTE_SETTING_NAME = 'genai_studio_mobile_preset_feed';

const DEFAULT_SETTING: MobilePresetFeedSetting = {
  enabled: true,
};

const isMobilePresetFeedSetting = (
  settings: unknown
): settings is MobilePresetFeedSetting =>
  typeof settings === 'object' &&
  settings !== null &&
  typeof (settings as MobilePresetFeedSetting).enabled === 'boolean';

let cachedValue: MobilePresetFeedSetting | undefined;
let pendingFetch: Promise<MobilePresetFeedSetting> | undefined;

/**
 * Resolves the experiment setting for callers outside React (e.g. the preset
 * deeplink handler). Shares the module cache with the hook and dedupes
 * concurrent fetches.
 */
export const getMobilePresetFeedSetting =
  (): Promise<MobilePresetFeedSetting> => {
    if (cachedValue !== undefined) return Promise.resolve(cachedValue);
    pendingFetch ??= getRemoteSettings<MobilePresetFeedSetting>({
      name: REMOTE_SETTING_NAME,
      tag: REMOTE_SETTING_NAME,
      defaultValue: DEFAULT_SETTING,
      validate: isMobilePresetFeedSetting,
    }).then((value) => {
      cachedValue = value;
      return value;
    });
    return pendingFetch;
  };

const useMobilePresetFeed = (): { isPresetFeedActive: boolean } => {
  const { isMobile } = useDeviceViewport();
  const [setting, setSetting] = useState<MobilePresetFeedSetting>(
    cachedValue ?? DEFAULT_SETTING
  );

  useEffect(() => {
    if (cachedValue !== undefined) return;
    getMobilePresetFeedSetting().then(setSetting);
  }, []);

  return { isPresetFeedActive: isMobile && setting.enabled };
};

export default useMobilePresetFeed;
